import React from 'react';
import { Trophy, Users, Target, TrendingUp, ClipboardList, Flag } from 'lucide-react';
import { branding } from '@/config/branding';

export const About: React.FC = () => {
  const values = [
    {
      icon: Trophy,
      title: 'Excellence',
      description: 'Every frame matters. We chase improvement and compete to win.',
    },
    {
      icon: Users,
      title: 'Teamwork',
      description: 'We support each other, win together, and grow as a family.',
    },
    {
      icon: Target,
      title: 'Focus',
      description: 'Concentration, discipline, and mental toughness, built through practice.',
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'Character on and off the lanes. The lessons outlast the season.',
    },
  ];

  return (
    <div className="bg-willard-black text-white min-h-screen">
      <div className="max-w-6xl mx-auto px-5 py-14">
        {/* Page header */}
        <header className="mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-willard-taupe mb-2">
            {branding.school.motto}
          </p>
          <h1 className="font-display text-5xl sm:text-6xl mb-3">
            ABOUT {branding.school.teamName.toUpperCase()} BOWLING
          </h1>
          <p className="text-willard-grey-400 max-w-2xl">
            Building champions on and off the lanes at Willard High School.
          </p>
        </header>

        {/* Mission */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <Flag className="w-6 h-6 text-willard-taupe" />
            <h2 className="font-heading text-3xl tracking-wide uppercase">Our Mission</h2>
          </div>
          <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5">
            <p className="text-willard-grey-400 leading-relaxed">
              We develop student-athletes who compete at the highest levels of Missouri high
              school bowling while living out what it means to be Focused, Connected, and Driven.
              Athletes of any skill level get real coaching, real reps, and a team that has
              their back.
            </p>
          </div>
        </section>

        {/* Core Values */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <Trophy className="w-6 h-6 text-willard-taupe" />
            <h2 className="font-heading text-3xl tracking-wide uppercase">Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map((value) => (
              <div
                key={value.title}
                className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5"
              >
                <value.icon className="w-6 h-6 text-willard-taupe mb-3" />
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-willard-taupe mb-1">
                  {value.title}
                </p>
                <p className="text-willard-grey-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How we track */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <ClipboardList className="w-6 h-6 text-willard-taupe" />
            <h2 className="font-heading text-3xl tracking-wide uppercase">How We Track</h2>
          </div>
          <div className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 p-5">
            <p className="text-willard-grey-400 leading-relaxed">
              Every match is logged frame by frame: strikes, spares, splits, and fill percentage.
              Averages are USBC-verified, so the numbers on this site are the real ones. No
              guesswork, no padding.
            </p>
          </div>
        </section>

        {/* Motto footer */}
        <section className="text-center py-8 border-t border-willard-grey-800">
          <p className="font-display text-2xl mb-2">{branding.school.motto}</p>
          <p className="text-willard-grey-400 text-sm">{branding.school.tagline}</p>
        </section>
      </div>
    </div>
  );
};
